import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGridPage } from '../../components/data-grid';
import { FilterTextField } from '../../components/filters';
import FilterSelectField from '../../components/filters/filter-select-field.tsx';
import { useInteractiveGrid } from '../../hooks';
import { useAppSelector } from '../../store/hooks';
import { User, UserFilters, UserRole } from '../../types';
import { getRoleLabel, getStatusLabel } from '../../utils';
import { selectAccessUsers, selectManageManufacturers, selectManageUsers } from './user-slice';
import { useGetUsersQuery, usePrefetch } from './users-api-slice';

const UserActionCell = (params: ICellRendererParams) => {
  const navigate = useNavigate();
  const canManageUsers = useAppSelector(selectManageUsers);

  return (
    <Tooltip title={canManageUsers ? 'Edit User' : 'View User'}>
      <IconButton
        size="small"
        onClick={() => navigate(`/user-profile/${params.data.id}`)}
        aria-label={canManageUsers ? 'edit user' : 'view user'}
      >
        {canManageUsers ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const UserManagementPage = () => {
  const hasAccess = useAppSelector(selectAccessUsers);
  const canManageUsers = useAppSelector(selectManageUsers);
  const prefetchUsers = usePrefetch('getUsers');
  const navigate = useNavigate();

  const {
    gridContainerRef,
    pagination,
    filters,
    filtersExpanded,
    setFiltersExpanded,
    response: usersResponse,
    error,
    sort,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
  } = useInteractiveGrid<UserFilters, User>({
    hasAccess,
    prefetchFunction: prefetchUsers,
    getDataQuery: useGetUsersQuery,
    initialFilters: {
      active: 'true',
    },
    initialSort: {
      field: 'dealer',
      direction: 'asc',
    },
    debounceOptions: {
      enabled: true,
      delay: 300,
    },
    spinnerOptions: {
      showSpinnerOnSort: true,
      showSpinnerOnFilter: true,
      showSpinnerOnPageChange: true,
    },
  });

  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const currentUser = useAppSelector(state => state.user.currentUser);

  // Column definitions for AG Grid
  const columnDefs = useMemo<ColDef[]>(() => {
    const columns: ColDef[] = [
      {
        field: 'role',
        headerName: 'Role',
        sortable: true,
        width: 150,
        valueFormatter: (params: ValueFormatterParams) => getRoleLabel(params.value as UserRole),
      },
    ];

    // Only add manufacturer column if user has permission to manage manufacturers
    if (canManageManufacturers) {
      columns.push({
        field: 'manufacturer',
        headerName: 'Manufacturer',
        sortable: true,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value && typeof params.value === 'object' ? params.value.name : params.value,
      });
    }

    // Add the rest of the columns
    columns.push(
      {
        field: 'dealer',
        headerName: 'Dealer',
        sortable: true,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value && typeof params.value === 'object' ? params.value.name : params.value,
      },
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'active',
        headerName: 'Status',
        sortable: true,
        width: 85,
        valueFormatter: (params: ValueFormatterParams) => getStatusLabel(params.data?.active),
        cellStyle: (params: CellClassParams<User, string>) => {
          return params.data?.active ? { color: 'green' } : { color: 'red' };
        },
        cellDataType: false,
      },
      {
        headerName: canManageUsers ? 'Actions' : 'View',
        width: 80,
        sortable: false,
        cellRenderer: UserActionCell,
      }
    );

    return columns;
  }, [canManageManufacturers, canManageUsers]);

  const roleOptions = useMemo(() => {
    const currentUserRole = currentUser?.role;

    // Filter roles based on current user's role
    let availableRoles = Object.values(UserRole);

    // Only administrators can see the administrator role
    if (currentUserRole !== UserRole.ADMINISTRATOR) {
      availableRoles = availableRoles.filter(role => role !== UserRole.ADMINISTRATOR);
    }

    return availableRoles.map(role => ({
      value: role,
      label: getRoleLabel(role),
    }));
  }, [currentUser?.role]);

  const statusOptions = useMemo(
    () => [
      { value: 'true', label: getStatusLabel(true) },
      { value: 'false', label: getStatusLabel(false) },
    ],
    []
  );

  if (!hasAccess) {
    return null;
  }

  const renderFilterControls = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Role"
            value={filters.role ?? ''}
            onChange={value =>
              handleFilterChange('role', value === '' ? undefined : (value as UserRole))
            }
            hasValue={!!filters.role}
            options={roleOptions}
            allOptionLabel="All Roles"
          />
        </Grid>

        {canManageManufacturers && (
          <Grid item xs={12} sm={6} md={1.5}>
            <FilterTextField
              label="Manufacturer"
              value={filters.manufacturer ?? ''}
              onChange={value => handleFilterChange('manufacturer', value)}
              hasValue={!!filters.manufacturer}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Dealer"
            value={filters.dealer ?? ''}
            onChange={value => handleFilterChange('dealer', value)}
            hasValue={!!filters.dealer}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Email"
            value={filters.email ?? ''}
            onChange={value => handleFilterChange('email', value)}
            hasValue={!!filters.email}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="First Name"
            value={filters.firstName ?? ''}
            onChange={value => handleFilterChange('firstName', value)}
            hasValue={!!filters.firstName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Last Name"
            value={filters.lastName ?? ''}
            onChange={value => handleFilterChange('lastName', value)}
            hasValue={!!filters.lastName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Status"
            value={filters.active ?? ''}
            onChange={value => handleFilterChange('active', value === '' ? undefined : value)}
            hasValue={filters.active !== undefined && filters.active !== ''}
            options={statusOptions}
            allOptionLabel="All Status"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            size="small"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataGridPage<User, UserFilters>
      title="User Management"
      addButtonText="Add User"
      addButtonPath="/user-profile"
      columnDefs={columnDefs}
      response={usersResponse}
      error={error}
      pagination={pagination}
      filters={filters}
      filtersExpanded={filtersExpanded}
      sort={sort}
      gridContainerRef={gridContainerRef}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChanged={onSortChanged}
      handleNextPageHover={handleNextPageHover}
      setFiltersExpanded={setFiltersExpanded}
      renderFilterControls={renderFilterControls}
      navigate={navigate}
      hideAddButton={!canManageUsers}
    />
  );
};

export default UserManagementPage;
